import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Flex,
  FormControl,
  FormLabel,
  Input,
  ModalFooter,
  Text,
} from "@chakra-ui/react";
import { Field, FieldProps, Form, Formik } from "formik";
import * as Yup from "yup";
import DaumPostcode, { Address } from "react-daum-postcode";
import CustomApprovalModal from "@/shared/components/custom-modal";
import AppButton from "@/features/line-sheet-sets/app-button";
import AppCheckBox from "@/features/line-sheet-sets/app-checkbox";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";

export interface ShippingInfoFormValues {
  alias: string;
  recipientName: string;
  contact: string;
  address: string;
  detailAddress: string;
  zipCode: string;
  isDefault: boolean;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  // 저장 — 실제 POST는 호출부가 소유한다. reject되면 모달은 열린 채 남아 재시도할 수 있다.
  onSubmit: (values: ShippingInfoFormValues) => void | Promise<void>;
  title?: string;
  cancelName?: string;
  confirmName?: string;
  // 수정 모드 재사용 대비 — 넘기지 않으면 빈 폼이다.
  initialValues?: Partial<ShippingInfoFormValues>;
  // 외부 pending을 우선한다. 미지정 시 formik.isSubmitting을 쓴다.
  isSubmitting?: boolean;
}

const LABEL_STYLE = {
  fontSize: "12px",
  color: "#6F6F6C",
  mb: "4px",
} as const;

const INPUT_STYLE = {
  fontSize: "12px",
  height: "28px",
} as const;

function ErrorMessage({ message }: { message?: string }) {
  if (!message) {
    return null;
  }

  return (
    <Text fontSize={"11px"} color={"red.400"} mt={"2px"}>
      {message}
    </Text>
  );
}

function ShippingInfoFormModal({
  isOpen,
  onClose,
  onSubmit,
  title,
  cancelName,
  confirmName,
  initialValues,
  isSubmitting,
}: Props) {
  const { t } = useI18nHelper();
  const [showPostcode, setShowPostcode] = useState(false);

  // 조건부 마운트가 아닌 호출부에서도 재사용될 수 있어, 열림 상태가 바뀌면 주소 검색 패널을 접는다.
  useEffect(() => {
    if (!isOpen) {
      setShowPostcode(false);
    }
  }, [isOpen]);

  const formInitialValues = useMemo(
    (): ShippingInfoFormValues => ({
      alias: initialValues?.alias ?? "",
      recipientName: initialValues?.recipientName ?? "",
      contact: initialValues?.contact ?? "",
      address: initialValues?.address ?? "",
      detailAddress: initialValues?.detailAddress ?? "",
      zipCode: initialValues?.zipCode ?? "",
      isDefault: initialValues?.isDefault ?? false,
    }),
    [initialValues]
  );

  // contact는 스펙상 optional이지만 배송 연락이 필수라 FE에서 required로 유지한다(화면 문구도 * 표기).
  const validationSchema = useMemo(
    () =>
      Yup.object({
        alias: Yup.string().required(
          t("common:shipping_info.form_modal.required_alias")
        ),
        recipientName: Yup.string().required(
          t("common:shipping_info.form_modal.required_recipient_name")
        ),
        contact: Yup.string().required(
          t("common:shipping_info.form_modal.required_contact")
        ),
        address: Yup.string().required(
          t("common:shipping_info.form_modal.required_address")
        ),
        zipCode: Yup.string().required(
          t("common:shipping_info.form_modal.required_zip_code")
        ),
      }),
    [t]
  );

  const handleAddressSelect = (
    data: Address,
    setFieldValue: (field: string, value: string) => void
  ) => {
    setFieldValue("address", data.address);
    setFieldValue("zipCode", data.zonecode);
    setShowPostcode(false);
  };

  return (
    <CustomApprovalModal
      isOpen={isOpen}
      onClose={onClose}
      // 화면보다 넓으면 왼쪽이 스크롤로도 닿지 않아 화면 폭으로 클램프한다.
      minWidth={"min(480px, 92dvw)"}
      maxWidth={"min(480px, 92dvw)"}
      isCentered={true}
      scrollBehavior={"inside"}
    >
      <Formik<ShippingInfoFormValues>
        enableReinitialize={true}
        initialValues={formInitialValues}
        validationSchema={validationSchema}
        validateOnChange={false}
        validateOnBlur={false}
        onSubmit={async (values, actions) => {
          try {
            await onSubmit(values);
          } finally {
            // 실패해도 모달을 닫지 않는다 — 입력값을 살려 재시도할 수 있게 한다.
            actions.setSubmitting(false);
          }
        }}
      >
        {(formik) => {
          const isLoading = isSubmitting ?? formik.isSubmitting;

          return (
            // 폼이 푸터까지 감싸야 푸터의 type="submit"이 제출로 이어진다.
            // display:contents로 Header/Body/Footer가 ModalContent의 직계 flex 아이템으로 남아야
            // scrollBehavior="inside"의 본문 스크롤이 정상 동작한다
            // (ModalContent에 overflow:hidden이 걸려 있어 폼이 박스를 만들면 내용이 잘린다).
            <Form style={{ display: "contents" }}>
              <CustomApprovalModal.Header
                title={title ?? t("common:shipping_info.form_modal.title")}
              />

              <CustomApprovalModal.Body>
                <Flex flexDirection={"column"} gap={"12px"}>
                  <Field name={"alias"}>
                    {(props: FieldProps<string>) => (
                      <FormControl isRequired={true}>
                        <FormLabel {...LABEL_STYLE}>
                          {t("common:alias")}
                        </FormLabel>
                        <Input
                          {...props.field}
                          {...INPUT_STYLE}
                          placeholder={t(
                            "common:shipping_info.form_modal.alias_placeholder"
                          )}
                        />
                        <ErrorMessage message={formik.errors.alias} />
                      </FormControl>
                    )}
                  </Field>

                  <Field name={"recipientName"}>
                    {(props: FieldProps<string>) => (
                      <FormControl isRequired={true}>
                        <FormLabel {...LABEL_STYLE}>
                          {t("common:name")}
                        </FormLabel>
                        <Input {...props.field} {...INPUT_STYLE} />
                        <ErrorMessage message={formik.errors.recipientName} />
                      </FormControl>
                    )}
                  </Field>

                  <Field name={"contact"}>
                    {(props: FieldProps<string>) => (
                      <FormControl isRequired={true}>
                        <FormLabel {...LABEL_STYLE}>
                          {t("common:contact")}
                        </FormLabel>
                        <Input
                          {...props.field}
                          {...INPUT_STYLE}
                          placeholder={t(
                            "common:shipping_info.form_modal.contact_placeholder"
                          )}
                        />
                        <ErrorMessage message={formik.errors.contact} />
                      </FormControl>
                    )}
                  </Field>

                  <FormControl isRequired={true}>
                    <FormLabel {...LABEL_STYLE}>
                      {t("common:shipping_info.form_modal.address")}
                    </FormLabel>
                    <Flex gap={"8px"} mb={"6px"}>
                      <Input
                        {...INPUT_STYLE}
                        value={formik.values.zipCode}
                        readOnly={true}
                        width={"120px"}
                        cursor={"default"}
                        placeholder={t(
                          "common:shipping_info.form_modal.zip_code_placeholder"
                        )}
                      />
                      <AppButton
                        size={"small"}
                        onClick={() => setShowPostcode((value) => !value)}
                      >
                        {t("common:shipping_info.form_modal.search_address")}
                      </AppButton>
                    </Flex>
                    {showPostcode && (
                      <Box
                        border={"1px solid #D4D4D1"}
                        borderRadius={"4px"}
                        mb={"6px"}
                        overflow={"hidden"}
                      >
                        <DaumPostcode
                          onComplete={(data) =>
                            handleAddressSelect(data, formik.setFieldValue)
                          }
                          style={{ height: "320px" }}
                        />
                      </Box>
                    )}
                    <Input
                      {...INPUT_STYLE}
                      value={formik.values.address}
                      readOnly={true}
                      cursor={"default"}
                      placeholder={t(
                        "common:shipping_info.form_modal.address_placeholder"
                      )}
                      mb={"6px"}
                    />
                    {/* zipCode 에러도 함께 보여준다 — 검증만 하면 저장이 조용히 막힌다. */}
                    <ErrorMessage
                      message={formik.errors.address ?? formik.errors.zipCode}
                    />
                    <Field name={"detailAddress"}>
                      {(props: FieldProps<string>) => (
                        <Input
                          {...props.field}
                          {...INPUT_STYLE}
                          mt={"6px"}
                          placeholder={t("common:detail_address")}
                        />
                      )}
                    </Field>
                  </FormControl>

                  <FormControl>
                    <AppCheckBox
                      isChecked={formik.values.isDefault}
                      name={t("common:shipping_info.form_modal.set_as_default")}
                      onChange={(value) =>
                        formik.setFieldValue("isDefault", value)
                      }
                    />
                  </FormControl>
                </Flex>
              </CustomApprovalModal.Body>

              {/* 공용 Footer는 버튼 폭이 96px 고정이라 라벨을 props로 바꾸는 이 모달에서는 직접 구성한다. */}
              <ModalFooter padding={"12px 16px"} gap={"8px"}>
                <AppButton
                  variant={"white"}
                  isDisabled={isLoading}
                  onClick={onClose}
                >
                  <Text textStyle={"body-sm-bold"}>
                    {cancelName ?? t("common:cancel")}
                  </Text>
                </AppButton>
                <AppButton
                  variant={"primary"}
                  type={"submit"}
                  isLoading={isLoading}
                >
                  <Text textStyle={"body-sm-bold"}>
                    {confirmName ?? t("common:save")}
                  </Text>
                </AppButton>
              </ModalFooter>
            </Form>
          );
        }}
      </Formik>
    </CustomApprovalModal>
  );
}

export default ShippingInfoFormModal;
